import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  LoadingState,
  SectionHeader,
  Select,
  StatusChip,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createMarketplaceApi } from "../api";
import {
  allowedOpportunityActions,
  allowedSignalActions,
  formatLabel,
  opportunityStatusTone,
  priorityTone,
  severityTone,
  signalStatusTone,
} from "../labels";
import type {
  MarketplaceHealthSeverity,
  MarketplaceOpportunityAction,
  MarketplaceSignalAction,
  OpportunityDto,
  Priority,
  SignalDto,
} from "../types";
import {
  ConfirmActionDialog,
  PatchOpportunityDialog,
} from "../components/MarketplaceDialogs";

const SEVERITIES: MarketplaceHealthSeverity[] = [
  "low",
  "normal",
  "high",
  "urgent",
  "critical",
];

const PRIORITIES: Priority[] = [
  "low",
  "normal",
  "high",
  "urgent",
  "critical",
];

export function SignalDetailPage() {
  const { signalId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createMarketplaceApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<SignalDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [ownerUserId, setOwnerUserId] = useState("");
  const [severity, setSeverity] =
    useState<MarketplaceHealthSeverity>("normal");

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Marketplace Health", to: "/app/marketplace" },
    { label: "Signals", to: "/app/marketplace/signals" },
    { label: row?.id ?? "Signal" },
  ]);

  const load = useCallback(async () => {
    if (!signalId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getSignal(signalId);
      setRow(result.signal);
      setOwnerUserId(result.signal.owner_user_id ?? "");
      setSeverity(result.signal.severity);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, signalId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading signal…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Signal unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedSignalActions(row.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Signal ${row.id}`}
        description={`${formatLabel(row.signal_type)} · ${formatLabel(row.target_type)}`}
        actions={
          actions.length > 0 ? (
            <Button
              variant="secondary"
              onClick={() => {
                setActionError(null);
                setActionOpen(true);
              }}
            >
              Lifecycle actions
            </Button>
          ) : null
        }
      />

      <ActionBar
        left={
          <Flex gap={8} wrap>
            <StatusChip tone={signalStatusTone(row.status)}>
              {formatLabel(row.status)}
            </StatusChip>
            <StatusChip tone={severityTone(row.severity)}>
              {formatLabel(row.severity)}
            </StatusChip>
          </Flex>
        }
      />

      <Alert tone="info" title="Investigation surface">
        Validate, monitor, require_action, resolve, archive, or revalidate with
        rationale. Ownership may be set on the action body. This does not fix
        supply, bookings, finance, or safety records — those stay in their
        modules.
      </Alert>

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Affected entity
          </Text>
          <Text role="body" as="p">
            Target: {formatLabel(row.target_type)} / {row.target_id}
          </Text>
          <Text role="body" as="p">
            Market ID: {row.market_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Owner User ID: {row.owner_user_id ?? "—"}
          </Text>
        </Stack>
      </Card>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Status history fields
          </Text>
          <Text role="body" as="p">
            Detected at: {row.detected_at}
          </Text>
          <Text role="body" as="p">
            Validated at: {row.validated_at ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            No dedicated audit-list endpoint on HQ Marketplace. Status and
            timestamps above are the contract-backed history fields for this
            record. Domain outbox events are not listed here.
          </Text>
        </Stack>
      </Card>

      <ConfirmActionDialog
        open={actionOpen}
        title="Signal lifecycle"
        notice="Actions update Marketplace Health Signal status only. Provide rationale for audit. Optional owner_user_id and severity override apply to the action body when set."
        actions={actions}
        extraFields={
          <Stack gap={12}>
            <Input
              label="Owner User ID (optional)"
              value={ownerUserId}
              onChange={(event) => setOwnerUserId(event.target.value)}
            />
            <Select
              label="Severity (optional override)"
              value={severity}
              onChange={(event) =>
                setSeverity(event.target.value as MarketplaceHealthSeverity)
              }
              options={SEVERITIES.map((value) => ({
                value,
                label: formatLabel(value),
              }))}
            />
          </Stack>
        }
        onClose={() => setActionOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const body: Record<string, unknown> = {
              severity,
            };
            if (input.reason) body.reason = input.reason;
            if (ownerUserId.trim()) body.owner_user_id = ownerUserId.trim();
            await api.applySignalAction(
              row.id,
              input.action as MarketplaceSignalAction,
              body,
            );
            toast.push({
              title: `Signal ${formatLabel(input.action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function OpportunityDetailPage() {
  const { opportunityId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createMarketplaceApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<OpportunityDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [patchOpen, setPatchOpen] = useState(false);
  const [ownerUserId, setOwnerUserId] = useState("");
  const [priority, setPriority] = useState<Priority>("normal");
  const [isMaterial, setIsMaterial] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Marketplace Health", to: "/app/marketplace" },
    { label: "Opportunities", to: "/app/marketplace/opportunities" },
    { label: row?.id ?? "Opportunity" },
  ]);

  const load = useCallback(async () => {
    if (!opportunityId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getOpportunity(opportunityId);
      setRow(result.opportunity);
      setOwnerUserId(result.opportunity.owner_user_id ?? "");
      setPriority(result.opportunity.priority);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, opportunityId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading opportunity…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Opportunity unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedOpportunityActions(row.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Opportunity ${row.id}`}
        description={`${formatLabel(row.target_type)} / ${row.target_id}`}
        actions={
          <Flex gap={8} wrap>
            <Button
              variant="secondary"
              onClick={() => {
                setActionError(null);
                setPatchOpen(true);
              }}
            >
              Update assessment
            </Button>
            {actions.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Lifecycle actions
              </Button>
            ) : null}
          </Flex>
        }
      />

      <ActionBar
        left={
          <Flex gap={8} wrap>
            <StatusChip tone={opportunityStatusTone(row.status)}>
              {formatLabel(row.status)}
            </StatusChip>
            <StatusChip tone={priorityTone(row.priority)}>
              {formatLabel(row.priority)}
            </StatusChip>
          </Flex>
        }
      />

      <Alert tone="warning" title="Ownership and resolution are deliberate">
        assign requires owner_user_id. Material prioritize / create paths may
        need Manager or Dual Approval. Decline and close need rationale. No
        automatic fixes or Task fan-out from this screen.
      </Alert>

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Affected entity
          </Text>
          <Text role="body" as="p">
            Target: {formatLabel(row.target_type)} / {row.target_id}
          </Text>
          <Text role="body" as="p">
            Market ID: {row.market_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Owner User ID: {row.owner_user_id ?? "—"}
          </Text>
        </Stack>
      </Card>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Assessments
          </Text>
          <Text role="caption" color="muted" as="p">
            Opaque JSON from the API — not scored in HQ.
          </Text>
          <Text role="body" as="p">
            impact_assessment: {JSON.stringify(row.impact_assessment)}
          </Text>
          <Text role="body" as="p">
            confidence_assessment: {JSON.stringify(row.confidence_assessment)}
          </Text>
        </Stack>
      </Card>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Status history fields
          </Text>
          <Text role="body" as="p">
            Identified at: {row.identified_at}
          </Text>
          <Text role="body" as="p">
            Closed at: {row.closed_at ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            No dedicated audit-list endpoint. Timestamps and status are the
            available history on this contract.
          </Text>
        </Stack>
      </Card>

      <ConfirmActionDialog
        open={actionOpen}
        title="Opportunity lifecycle"
        notice="validate → prioritize → assign → start → realize / decline / close. Assign requires Owner User ID. Set is_material + Approval ID when prioritizing material expansion."
        actions={actions}
        extraFields={
          <Stack gap={12}>
            <Input
              label="Owner User ID (required for assign)"
              value={ownerUserId}
              onChange={(event) => setOwnerUserId(event.target.value)}
            />
            <Select
              label="Priority (optional override)"
              value={priority}
              onChange={(event) =>
                setPriority(event.target.value as Priority)
              }
              options={PRIORITIES.map((value) => ({
                value,
                label: formatLabel(value),
              }))}
            />
            <Checkbox
              label="Material (approval when prioritize/create elevated)"
              checked={isMaterial}
              onChange={(event) => setIsMaterial(event.target.checked)}
            />
          </Stack>
        }
        onClose={() => setActionOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const action = input.action as MarketplaceOpportunityAction;
            if (action === "assign" && !ownerUserId.trim()) {
              setActionError("owner_user_id is required to assign");
              setBusy(false);
              return;
            }
            const body: Record<string, unknown> = {
              priority,
              is_material: isMaterial,
            };
            if (input.reason) body.reason = input.reason;
            if (input.approval_id) body.approval_id = input.approval_id;
            if (ownerUserId.trim()) body.owner_user_id = ownerUserId.trim();
            await api.applyOpportunityAction(row.id, action, body);
            toast.push({
              title: `Opportunity ${formatLabel(action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <PatchOpportunityDialog
        open={patchOpen}
        onClose={() => setPatchOpen(false)}
        busy={busy}
        error={actionError}
        initialImpact={row.impact_assessment}
        initialConfidence={row.confidence_assessment}
        initialPriority={row.priority}
        onSubmit={async (body) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.patchOpportunity(row.id, body);
            toast.push({ title: "Assessment updated", tone: "success" });
            setPatchOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}
